import type { IspSportsAlias } from '@ps/web-core-store';
import { difference } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TranslationTag } from '@swf/core/types/i18n';
import { TabLinkMenu } from '@swf/sports/components';
import { EventTypeId } from '@swf/sports/types/eventType';

import { TXTWCL_Events, TXTWCL_Matches, TXTWCL_Outrights } from './CompetitionTabs.messages.json';

export enum Tab {
  EVENTS = 0,
  OUTRIGHTS = 1,
  MATCHES = 2
}

export interface Props {
  hiddenTabs?: Tab[];
}

interface TabConfig {
  alias: IspSportsAlias;
  translation: TranslationTag;
  id: Tab;
}

const tabsConfig: Record<Tab, TabConfig> = {
  [Tab.EVENTS]: {
    alias: 'sportsCompetition',
    translation: TXTWCL_Events,
    id: Tab.EVENTS
  },
  [Tab.OUTRIGHTS]: {
    alias: 'sportsCompetitionOutrights',
    translation: TXTWCL_Outrights,
    id: Tab.OUTRIGHTS
  },
  [Tab.MATCHES]: {
    alias: 'sportsGolfMatches',
    translation: TXTWCL_Matches,
    id: Tab.MATCHES
  }
};

const getTabConfig = (eventTypeId: string, hiddenTabs: Tab[]): TabConfig[] => {
  const bespokeTabsForEventType: Partial<Record<EventTypeId, Tab[]>> = {
    [EventTypeId.GOLF]: [Tab.MATCHES, Tab.OUTRIGHTS]
  };
  const tabs = bespokeTabsForEventType[Number(eventTypeId) as EventTypeId] ?? [
    Tab.EVENTS,
    Tab.OUTRIGHTS
  ];
  const tabsToShow = difference(tabs, hiddenTabs);
  return tabsToShow.map((tab) => tabsConfig[tab]);
};

const CompetitionTabs: React.FC<Props> = ({ hiddenTabs = [] }) => {
  const page = useSelector(select.cms.getPage);
  const isGolfMatches = page?.alias === 'sportsGolfMatches';
  const match = useSelector((rootState: RootState) => select.cms.matchAlias(rootState, undefined));

  const {
    competitionId = '',
    competitionSlug = '',
    eventTypeId = isGolfMatches ? String(EventTypeId.GOLF) : '',
    eventTypeSlug = ''
  } = (match?.params as Record<string, string> | undefined) || ({} as Record<string, string>);

  const tabs = useMemo(() => getTabConfig(eventTypeId, hiddenTabs), [eventTypeId, hiddenTabs]);

  return (
    <TabLinkMenu>
      {tabs.map(({ alias, id, translation }) => (
        <TabLinkMenu.Link
          key={id}
          alias={alias}
          values={{ eventTypeSlug, eventTypeId, competitionSlug, competitionId }}
        >
          <Translation {...translation} />
        </TabLinkMenu.Link>
      ))}
    </TabLinkMenu>
  );
};

export { CompetitionTabs };
